import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ─── Interfaces ────────────────────────────────────────────────────────────────

export interface Item {
  id: string;
  code: string;
  name: string;
  type: 'raw_material' | 'finished_good' | 'semi_finished' | 'sub_assembly' | 'component' | 'consumable' | 'spare_part';
  uom: string;
  category: string;
  description?: string;
  status: 'active' | 'inactive';
  minStock?: number;
  maxStock?: number;
  reorderPoint?: number;
  leadTimeDays?: number;
  unitCost?: number;
  createdAt?: string;
}

export interface BOMComponent {
  id: string;
  parentItemId: string;
  childItemId: string;
  childItemCode: string;
  childItemName: string;
  quantity: number;
  uom: string;
  scrapPercent: number;
  notes?: string;
  children?: BOMComponent[];
}

export interface BOM {
  id: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  version: string;
  status: 'active' | 'inactive' | 'draft';
  effectiveFrom: string;
  effectiveTo?: string;
  components: BOMComponent[];
  createdAt?: string;
}

export interface WorkCenter {
  id: string;
  code: string;
  name: string;
  type: 'machine' | 'manual' | 'automated' | 'inspection';
  department: string;
  capacity: number;
  capacityUom: string;
  status: 'active' | 'inactive' | 'maintenance';
  description?: string;
  linkedRoutingsCount?: number;
  utilizationPercent?: number;
}

export interface RoutingOperationInput {
  id: string;
  productCode: string;
  productName: string;
  quantityPer: number;
  uom: string;
  sourceType: 'external' | 'prior_operation' | string;
  sourceOperationSequence?: number;
  scrapFactor: number;
}

export interface RoutingOperationOutput {
  id: string;
  productCode: string;
  productName: string;
  quantityPer: number;
  uom: string;
  outputType: 'primary' | 'coproduct' | 'byproduct' | 'scrap' | 'loss' | string;
  feedsNextOperation: boolean;
  nextOperationSequence?: number;
}

export interface RoutingOperation {
  id: string;
  routingId: string;
  operationNumber: string;
  operationName: string;
  workCenterId: string;
  workCenterCode: string;
  workCenterName: string;
  setupTimeMinutes: number;
  runTimeMinutes: number;
  runTimeUom: string;
  sequenceNumber: number;
  notes?: string;
  inputs: RoutingOperationInput[];
  outputs: RoutingOperationOutput[];
}

export interface Routing {
  id: string;
  code: string;
  name: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  status: 'active' | 'inactive' | 'draft';
  version: string;
  description?: string;
  batchSize?: number;
  operations: RoutingOperation[];
  createdAt?: string;
}

export interface Location {
  id: string;
  code: string;
  name: string;
  type: 'site' | 'kitchen' | 'building' | 'floor' | 'zone' | 'aisle' | 'rack' | 'shelf' | 'bin' | 'position' | 'plant' | 'area' | 'line' | 'workstation' | 'storage';
  parentId?: string;
  parentName?: string;
  description?: string;
  status: 'active' | 'inactive' | 'maintenance';
  restrictions?: any;
  children?: Location[];
}

export interface MasterDataState {
  items: Item[];
  boms: BOM[];
  workCenters: WorkCenter[];
  routings: Routing[];
  locations: Location[];
  selectedBOM: BOM | null;
  isLoading: boolean;
  itemsLoading: boolean;
  bomsLoading: boolean;
  workCentersLoading: boolean;
  routingsLoading: boolean;
  locationsLoading: boolean;
  error: string | null;
}

// ─── Initial State ─────────────────────────────────────────────────────────────

const initialState: MasterDataState = {
  items: [],
  boms: [],
  workCenters: [],
  routings: [],
  locations: [],
  selectedBOM: null,
  isLoading: false,
  itemsLoading: false,
  bomsLoading: false,
  workCentersLoading: false,
  routingsLoading: false,
  locationsLoading: false,
  error: null,
};


// ─── Normalization ─────────────────────────────────────────────────────────────

function normalizeItem(raw: any): Item {
  return {
    id: raw.id,
    code: raw.itemCode ?? raw.code ?? '',
    name: raw.description ?? raw.name ?? '',
    type: raw.itemType ?? raw.type ?? 'raw_material',
    uom: raw.unitOfMeasure ?? raw.uom ?? '',
    category: raw.productFamily?.name ?? raw.abcClassification ?? raw.category ?? '',
    description: raw.description ?? '',
    status: raw.status ?? 'active',
    minStock: raw.safetyStock ?? raw.minStock,
    reorderPoint: raw.reorderPoint,
    leadTimeDays: raw.leadTimeDays,
    unitCost: raw.standardCost ?? raw.unitCost,
    createdAt: raw.createdAt,
  };
}

function normalizeWorkCenter(raw: any): WorkCenter {
  return {
    id: raw.id,
    code: raw.workCenterCode ?? raw.code ?? '',
    name: raw.name ?? '',
    type: raw.workCenterType ?? raw.type ?? 'manual',
    department: raw.location?.name ?? raw.departmentId ?? raw.department ?? '',
    capacity: raw.theoreticalCapacity ?? raw.availableCapacity ?? raw.capacity ?? 0,
    capacityUom: raw.capacityUom ?? 'hrs',
    status: raw.status ?? 'active',
    description: raw.description,
    linkedRoutingsCount: raw.linkedRoutingsCount ?? 0,
    utilizationPercent: raw.utilizationFactor != null
      ? Math.round(raw.utilizationFactor * 100)
      : (raw.utilizationPercent ?? 0),
  };
}

function normalizeLocation(raw: any): Location {
  return {
    id: raw.id,
    code: raw.locationCode ?? raw.code ?? '',
    name: raw.name ?? '',
    type: raw.locationType ?? raw.type ?? 'area',
    parentId: raw.parentLocationId ?? raw.parentId,
    status: raw.status ?? 'active',
    description: raw.description,
    restrictions: raw.restrictions,
    children: Array.isArray(raw.childLocations) ? raw.childLocations.map(normalizeLocation) : undefined,
  };
}

function normalizeRouting(raw: any): Routing {
  return {
    id: raw.id,
    code: raw.routeCode ?? raw.code ?? '',
    name: raw.routeName ?? raw.name ?? '',
    itemId: raw.finalProductId ?? raw.itemId ?? '',
    itemCode: raw.finalProductCode ?? raw.itemCode ?? '',
    itemName: raw.finalProductName ?? raw.itemName ?? raw.routeName ?? '',
    status: raw.status ?? 'draft',
    version: raw.version ? String(raw.version) : '1',
    description: raw.description,
    batchSize: Number(raw.standardBatchSize ?? raw.batchSize ?? 0) || undefined,
    operations: Array.isArray(raw.operations) ? raw.operations.map((op: any) => ({
      id: op.id,
      routingId: op.routeId ?? op.routingId ?? raw.id,
      // process-route API: operationCode + sequence; legacy: operationNumber + sequenceNumber
      operationNumber: op.operationCode ?? op.operationNumber ?? String(op.sequence ?? op.sequenceNumber ?? ''),
      operationName: op.operationName ?? op.name ?? '',
      workCenterId: op.workCenterId ?? '',
      workCenterCode: op.workCenterCode ?? '',
      workCenterName: op.workCenterName ?? '',
      setupTimeMinutes: Number(op.setupTimeMinutes ?? op.setupTime ?? 0),
      runTimeMinutes: Number(op.runTimePerUnit ?? op.runTime ?? op.runTimeMinutes ?? 0),
      runTimeUom: op.runTimeUom ?? 'min',
      sequenceNumber: Number(op.sequence ?? op.sequenceNumber ?? 0),
      notes: op.notes ?? op.description,
      inputs: (Array.isArray(op.inputs) ? op.inputs : [])
        .slice()
        .sort((a: any, b: any) => (a.lineNumber ?? 0) - (b.lineNumber ?? 0))
        .map((inp: any) => ({
          id: inp.id,
          productCode: inp.productCode ?? inp.itemCode ?? '',
          productName: inp.productName ?? inp.itemDescription ?? '',
          quantityPer: Number(inp.quantityPer ?? 0),
          uom: inp.uom ?? '',
          sourceType: inp.sourceType ?? 'external',
          sourceOperationSequence: inp.sourceOperationSequence ?? undefined,
          scrapFactor: Number(inp.scrapFactor ?? 0),
        })),
      outputs: (Array.isArray(op.outputs) ? op.outputs : [])
        .slice()
        .sort((a: any, b: any) => (a.lineNumber ?? 0) - (b.lineNumber ?? 0))
        .map((out: any) => ({
          id: out.id,
          productCode: out.productCode ?? out.itemCode ?? '',
          productName: out.productName ?? out.itemDescription ?? '',
          quantityPer: Number(out.quantityPer ?? 0),
          uom: out.uom ?? '',
          outputType: out.outputType ?? 'primary',
          feedsNextOperation: Boolean(out.feedsNextOperation),
          nextOperationSequence: out.nextOperationSequence ?? undefined,
        })),
    })) : [],
    createdAt: raw.createdAt,
  };
}

// ─── Async Thunks ──────────────────────────────────────────────────────────────

export const fetchItems = createAsyncThunk('masterData/fetchItems', async () => {
  const response = await axios.get(`${API_BASE_URL}/master-data/items`, { headers: authHeaders() });
  const list = response.data;
  const raw = Array.isArray(list) ? list : list?.data ?? [];
  return raw.map(normalizeItem) as Item[];
});

export const createItem = createAsyncThunk(
  'masterData/createItem',
  async (item: Partial<Item>, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/master-data/items`, item, { headers: authHeaders() });
      return response.data as Item;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || err?.message || 'Failed to create item');
    }
  }
);

export const updateItem = createAsyncThunk(
  'masterData/updateItem',
  async ({ id, data }: { id: string; data: Partial<Item> }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`${API_BASE_URL}/master-data/items/${id}`, data, { headers: authHeaders() });
      return response.data as Item;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || err?.message || 'Failed to update item');
    }
  }
);

export const deleteItem = createAsyncThunk(
  'masterData/deleteItem',
  async (id: string, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_BASE_URL}/master-data/items/${id}`, { headers: authHeaders() });
      return id;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || err?.message || 'Failed to delete item');
    }
  }
);

// The API returns one flat row per BOM line (parentItemId/childItemId with
// eager-loaded parentItem/childItem); group them into one BOM per parent item.
function normalizeBOMs(raw: any[]): BOM[] {
  if (raw.length > 0 && Array.isArray(raw[0]?.components)) return raw as BOM[];
  const byParent = new Map<string, BOM>();
  raw.forEach((row) => {
    const parentId = row.parentItemId ?? row.parent_item_id;
    if (!parentId) return;
    let bom = byParent.get(parentId);
    if (!bom) {
      bom = {
        id: parentId,
        itemId: parentId,
        itemCode: row.parentItem?.itemCode ?? row.parentItem?.code ?? '',
        itemName: row.parentItem?.description ?? row.parentItem?.name ?? '',
        version: '1',
        status: row.isActive === false ? 'inactive' : 'active',
        effectiveFrom: row.effectiveDate ?? row.createdAt ?? '',
        components: [],
      };
      byParent.set(parentId, bom);
    }
    bom.components.push({
      id: String(row.id),
      parentItemId: parentId,
      childItemId: row.childItemId ?? row.child_item_id ?? '',
      childItemCode: row.childItem?.itemCode ?? row.childItem?.code ?? '',
      childItemName: row.childItem?.description ?? row.childItem?.name ?? '',
      quantity: Number(row.quantity ?? 0),
      uom: row.unitOfMeasure ?? row.childItem?.unitOfMeasure ?? '',
      scrapPercent: Number(row.scrapFactor ?? 0) * 100,
      notes: row.notes,
    });
  });
  return Array.from(byParent.values());
}

export const fetchBOMs = createAsyncThunk('masterData/fetchBOMs', async () => {
  const response = await axios.get(`${API_BASE_URL}/master-data/bill-of-materials`, { headers: authHeaders() });
  const list = response.data;
  return normalizeBOMs(Array.isArray(list) ? list : list?.data ?? []);
});

export const createBOMLine = createAsyncThunk(
  'masterData/createBOMLine',
  async (
    line: { parentItemId: string; childItemId: string; quantity: number; unitOfMeasure: string; scrapPercent?: number },
    { dispatch, rejectWithValue }
  ) => {
    try {
      await axios.post(
        `${API_BASE_URL}/master-data/bill-of-materials`,
        {
          parentItemId: line.parentItemId,
          childItemId: line.childItemId,
          quantity: line.quantity,
          unitOfMeasure: line.unitOfMeasure || 'PC',
          scrapFactor: (line.scrapPercent ?? 0) / 100,
          effectiveDate: new Date().toISOString().slice(0, 10),
        },
        { headers: authHeaders() }
      );
      await dispatch(fetchBOMs());
      return true;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || err?.message || 'Failed to add BOM component');
    }
  }
);

export const updateBOMLine = createAsyncThunk(
  'masterData/updateBOMLine',
  async (
    line: { id: string; quantity: number; scrapPercent?: number },
    { dispatch, rejectWithValue }
  ) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/master-data/bill-of-materials/${line.id}`,
        { quantity: line.quantity, scrapFactor: (line.scrapPercent ?? 0) / 100 },
        { headers: authHeaders() }
      );
      await dispatch(fetchBOMs());
      return true;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || err?.message || 'Failed to update BOM component');
    }
  }
);

export const deleteBOMLine = createAsyncThunk(
  'masterData/deleteBOMLine',
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      await axios.delete(`${API_BASE_URL}/master-data/bill-of-materials/${id}`, { headers: authHeaders() });
      await dispatch(fetchBOMs());
      return id;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || err?.message || 'Failed to remove BOM component');
    }
  }
);

export const fetchWorkCenters = createAsyncThunk('masterData/fetchWorkCenters', async () => {
  const response = await axios.get(`${API_BASE_URL}/master-data/work-centers`, { headers: authHeaders() });
  const list = response.data;
  const raw = Array.isArray(list) ? list : list?.data ?? [];
  return raw.map(normalizeWorkCenter) as WorkCenter[];
});

export const createWorkCenter = createAsyncThunk(
  'masterData/createWorkCenter',
  async (wc: Partial<WorkCenter>, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/master-data/work-centers`, wc, { headers: authHeaders() });
      return response.data as WorkCenter;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || err?.message || 'Failed to create work center');
    }
  }
);

export const updateWorkCenter = createAsyncThunk(
  'masterData/updateWorkCenter',
  async ({ id, data }: { id: string; data: Partial<WorkCenter> }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`${API_BASE_URL}/master-data/work-centers/${id}`, data, { headers: authHeaders() });
      return response.data as WorkCenter;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || err?.message || 'Failed to update work center');
    }
  }
);

export const fetchRoutings = createAsyncThunk('masterData/fetchRoutings', async () => {
  const response = await axios.get(`${API_BASE_URL}/work-orders/process-routes`, { headers: authHeaders() });
  const list = response.data;
  const raw = Array.isArray(list) ? list : list?.data ?? [];
  return raw.map(normalizeRouting) as Routing[];
});

export const fetchLocations = createAsyncThunk('masterData/fetchLocations', async () => {
  const response = await axios.get(`${API_BASE_URL}/master-data/locations`, { headers: authHeaders() });
  const list = response.data;
  const raw = Array.isArray(list) ? list : list?.data ?? [];
  return raw.map(normalizeLocation) as Location[];
});

export const createLocation = createAsyncThunk(
  'masterData/createLocation',
  async (loc: Partial<Location>, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/master-data/locations`, {
        locationCode: loc.code,
        name: loc.name,
        locationType: loc.type,
        parentLocationId: loc.parentId,
        status: loc.status,
        restrictions: loc.restrictions,
      }, { headers: authHeaders() });
      return normalizeLocation(response.data) as Location;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || err?.message || 'Failed to create location');
    }
  }
);

// ─── Slice ─────────────────────────────────────────────────────────────────────

const masterDataSlice = createSlice({
  name: 'masterData',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedBOM: (state, action: PayloadAction<BOM | null>) => {
      state.selectedBOM = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Items
      .addCase(fetchItems.pending, (state) => { state.itemsLoading = true; state.error = null; })
      .addCase(fetchItems.fulfilled, (state, action) => { state.itemsLoading = false; state.items = action.payload; })
      .addCase(fetchItems.rejected, (state, action) => { state.itemsLoading = false; state.error = action.error.message || 'Failed to fetch items'; })
      .addCase(createItem.fulfilled, (state, action) => { state.items.unshift(action.payload); })
      .addCase(createItem.rejected, (state, action) => { state.error = (action.payload as string) || 'Failed to create item'; })
      .addCase(updateItem.fulfilled, (state, action) => {
        const idx = state.items.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(updateItem.rejected, (state, action) => { state.error = (action.payload as string) || 'Failed to update item'; })
      .addCase(deleteItem.fulfilled, (state, action) => {
        state.items = state.items.filter((i) => i.id !== action.payload);
      })
      .addCase(deleteItem.rejected, (state, action) => { state.error = (action.payload as string) || 'Failed to delete item'; })
      // BOMs
      .addCase(fetchBOMs.pending, (state) => { state.bomsLoading = true; })
      .addCase(fetchBOMs.fulfilled, (state, action) => { state.bomsLoading = false; state.boms = action.payload; })
      .addCase(fetchBOMs.rejected, (state, action) => { state.bomsLoading = false; state.error = action.error.message || 'Failed to fetch BOMs'; })
      // Work Centers
      .addCase(fetchWorkCenters.pending, (state) => { state.workCentersLoading = true; })
      .addCase(fetchWorkCenters.fulfilled, (state, action) => { state.workCentersLoading = false; state.workCenters = action.payload; })
      .addCase(fetchWorkCenters.rejected, (state, action) => { state.workCentersLoading = false; state.error = action.error.message || 'Failed to fetch work centers'; })
      .addCase(createWorkCenter.fulfilled, (state, action) => { state.workCenters.unshift(action.payload); })
      .addCase(createWorkCenter.rejected, (state, action) => { state.error = (action.payload as string) || 'Failed to create work center'; })
      .addCase(updateWorkCenter.fulfilled, (state, action) => {
        const idx = state.workCenters.findIndex((w) => w.id === action.payload.id);
        if (idx !== -1) state.workCenters[idx] = { ...state.workCenters[idx], ...action.payload };
      })
      .addCase(updateWorkCenter.rejected, (state, action) => { state.error = (action.payload as string) || 'Failed to update work center'; })
      // Routings
      .addCase(fetchRoutings.pending, (state) => { state.routingsLoading = true; })
      .addCase(fetchRoutings.fulfilled, (state, action) => { state.routingsLoading = false; state.routings = action.payload; })
      .addCase(fetchRoutings.rejected, (state, action) => { state.routingsLoading = false; state.error = action.error.message || 'Failed to fetch routings'; })
      // Locations
      .addCase(fetchLocations.pending, (state) => { state.locationsLoading = true; })
      .addCase(fetchLocations.fulfilled, (state, action) => { state.locationsLoading = false; state.locations = action.payload; })
      .addCase(fetchLocations.rejected, (state, action) => { state.locationsLoading = false; state.error = action.error.message || 'Failed to fetch locations'; })
      .addCase(createLocation.fulfilled, (state, action) => {
        if (action.payload.parentId) {
          const parent = state.locations.find((l) => l.id === action.payload.parentId);
          if (parent) {
            if (!parent.children) parent.children = [];
            parent.children.push(action.payload);
          }
        } else {
          state.locations.push(action.payload);
        }
      })
      .addCase(createLocation.rejected, (state, action) => { state.error = (action.payload as string) || 'Failed to create location'; });
  },
});

export const { clearError, setSelectedBOM } = masterDataSlice.actions;
export default masterDataSlice.reducer;
